import "./allbattles.css";
import { getAllBattles } from "../../api/modules/b2droppie.class";
import Battle from "../Battle/Battle";
import { routes } from "../../routes";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopUpModal from "../TopupModal/TopUpModal";
import FullAppLoader from "../Loader/FullAppLoader";
import { useDispatch, useSelector } from "react-redux";
import loadmore from "../../assets/icons/load-more.png";
import { toggleLoader } from "../../redux/slices/loaderSlice";
import BackdropLoader from "../BackdropLoader/BackdropLoader";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";
import { Plus } from "lucide-react";
import { ChevronLeft } from "lucide-react";
const AllBattle = ({ header }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(15);
  const [allFetchedBattles, setAllFetchedBattles] = useState([]);
  const [topUp, setTopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalBattle, setTotalBattle] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(0);

  const user = useSelector((state) => state?.auth?.user);
  const balance = useSelector((state) => state?.account?.balance);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const isLoading = useSelector((state) => state?.loader?.isLoading);
  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const lastEvent = useSelector((state) => state?.battle?.lastEvent);
  const lastGeneralEvent = useSelector((state) => state?.battle?.lastGeneralEvent);

  const lastProcessedEventId = useRef(null);
  const lastProcessedGeneralEventId = useRef(null);

  // ── Guards against concurrent / stale API calls ──
  const isFetchingRef = useRef(false);
  const fetchIdRef = useRef(0);

  // ── Measure navbar height dynamically ──
  useEffect(() => {
    const measureNavbar = () => {
      const navbar =
        document.querySelector("nav") ||
        document.querySelector(".navbar") ||
        document.querySelector("[class*='navbar']") ||
        document.querySelector("[class*='Navbar']") ||
        document.querySelector("header");

      if (navbar) {
        setNavbarHeight(navbar.offsetHeight);
      }
    };

    measureNavbar();

    // Re-measure on resize (responsive navbar height may change)
    window.addEventListener("resize", measureNavbar);
    return () => window.removeEventListener("resize", measureNavbar);
  }, []);

  // ── Deduplicate helper ──
  const deduplicateBattles = (battles) => {
    const seen = new Map();
    for (const battle of battles) {
      if (battle?.id != null && !seen.has(battle.id)) {
        seen.set(battle.id, battle);
      }
    }
    return Array.from(seen.values());
  };

  // ── Core fetch function ──
  const getAll = useCallback(
    async (pageNum = 1, append = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      const currentFetchId = ++fetchIdRef.current;

      try {
        setLoading(true);
        dispatch(toggleLoader({ isLoading: true }));

        const response = await getAllBattles(pageNum);

        if (currentFetchId !== fetchIdRef.current) return;

        const currentPage = response?.data?.current_page;
        const totalLastPage = response?.data?.last_page;
        const battles = response?.data?.data;
        const total = response?.data?.total ?? 0;

        setTotalBattle(total);
        setLastPage(totalLastPage);
        setPage(currentPage);

        if (Array.isArray(battles) && battles.length > 0) {
          const filteredBattles = battles.filter((battle) => {
            if (!battle?.private) return true;
            return battle?.creator_id === user?.id;
          });

          setAllFetchedBattles((prev) => {
            const combined = append
              ? [...prev, ...filteredBattles]
              : filteredBattles;
            return deduplicateBattles(combined);
          });
        } else if (!append) {
          setAllFetchedBattles([]);
        }
      } catch (err) {
        console.error("Error fetching battles:", err);
        if (!append) setAllFetchedBattles([]);
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false);
          dispatch(toggleLoader({ isLoading: false }));
        }
        isFetchingRef.current = false;
      }
    },
    [user?.id, dispatch]
  );

  // ── Initial load ──
  useEffect(() => {
    getAll(1, false);
    setVisibleCount(15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Real-time: user-specific events ──
  useEffect(() => {
    if (!lastEvent) return;
    const { _id: eventId, type: eventType } = lastEvent;
    if (lastProcessedEventId.current === eventId) return;

    if (eventType === "battle_cancelled" || eventType === "battle_joined") {
      lastProcessedEventId.current = eventId;
      isFetchingRef.current = false;
      setAllFetchedBattles([]);
      setVisibleCount(15);
      getAll(1, false);
    }
  }, [lastEvent, getAll]);

  // ── Real-time: general events ──
  useEffect(() => {
    if (!lastGeneralEvent) return;
    const { _id: eventId, type: eventType } = lastGeneralEvent;
    if (lastProcessedGeneralEventId.current === eventId) return;

    if (
      eventType === "battle_created" ||
      eventType === "battle_updated" ||
      eventType === "battle_deleted"
    ) {
      lastProcessedGeneralEventId.current = eventId;
      isFetchingRef.current = false;
      setAllFetchedBattles([]);
      setVisibleCount(15);
      getAll(1, false);
    }
  }, [lastGeneralEvent, getAll]);

  // ── Load More handler ──
  const handleLoadMore = () => {
    const nextVisible = visibleCount + 15;
    setVisibleCount(nextVisible);
    if (nextVisible > allFetchedBattles.length && page < lastPage) {
      getAll(page + 1, true);
    }
  };

  // ── Create battle ──
  const handleCreate = () => {
    if (isLoggedIn) {
      if (balance > 0) {
        navigate(routes.CREATEBATTLE);
      } else {
        setTopup(true);
      }
    } else {
      dispatch(toggleOnboard({ login: true }));
    }
  };

  // ── Visible slice ──
  const visibleBattles = header
    ? allFetchedBattles.slice(0, 4)
    : allFetchedBattles.slice(0, visibleCount);

  // ── Whether to show the Load More button ──
  const showLoadMore =
    !header &&
    visibleBattles.length > 0 &&
    (visibleCount < allFetchedBattles.length || page < lastPage) &&
    visibleCount < totalBattle;

  return isLoading ? (
    <FullAppLoader />
  ) : (
    <div className={isDarkMode ? "page-dark" : "page-light"}>
      {/* ✅ Sticky Header — top dynamically navbar height se set hota hai */}
      {!header && (
        <div
          className={`battle-sticky-header ${isDarkMode ? "dark" : "light"}`}
          style={{ top: navbarHeight }}
        >
          <div className="container allBattleMain">
            <div className="battle-header-left">
              <div onClick={() => navigate(routes.HOME)} className="header-back-btn">
                <ChevronLeft style={{ color: "#A6A6A6" }} size={20} />
              </div>
              <div className={`battle-title ${isDarkMode ? "dark" : "light"}`}>
                Active Battles
              </div>
            </div>

            <button className="create-battle-btn" onClick={handleCreate}>
              <Plus size={18} className="icon-spacing" />
              Create Battles
            </button>
          </div>
        </div>
      )}

      {/* ✅ Scrollable battles content */}
      <div
        className={`container p-0 ${
          isDarkMode ? "allBattleMain-dark" : "allBattleMain-light"
        }`}
      >
        <div className="all-battles-list">
          {visibleBattles.length > 0 ? (
            visibleBattles.map((item, index) =>
              item != null ? (
                <div key={item.id}>
                  <Battle item={item} index={index} />
                </div>
              ) : null,
            )
          ) : (
            <>
              <div className="d-flex p-5 justify-content-center">
                No Battles Available
              </div>
              {header && (
                <div className="d-center">
                  <button className="create-battle-btn " onClick={handleCreate}>
                    <Plus size={18} className="icon-spacing" />
                    Create Battles
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {!header && (
          <div className="d-center m-3 p-3">
            <p
              style={{
                color: "#BAB4C3",
                fontStyle: "italic",
                fontWeight: "500",
                fontSize: "10px",
              }}
            >
              *Even if you lose the battle, you’ll earn XP points equal to the
              amount you wagered.
            </p>
          </div>
        )}
      </div>

      {showLoadMore && (
        <div
          className={
            isDarkMode
              ? "load-more-div-dark load-more-div"
              : "load-more-div-light load-more-div"
          }
        >
          <button className="mb-5" onClick={handleLoadMore} disabled={loading}>
            {loading ? "Loading..." : "Load More"}{" "}
            {!loading && (
              <img
                src={loadmore}
                alt=""
                style={{ filter: isDarkMode ? "none" : "invert(1)" }}
              />
            )}
          </button>
        </div>
      )}

      <TopUpModal setTopUpModalShow={setTopup} show={topUp} />
      <BackdropLoader show={loading} />
    </div>
  );
};

export default AllBattle;